import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckIcon, ClockIcon, XIcon } from "@phosphor-icons/react";
import { WhatsApp } from "@mui/icons-material";

import { HttpMercadoPagoReturn } from "../http";
import type { MercadoPagoReturnVerificacionDTO } from "../http/httpMercadoPagoReturn";
import { useCart } from "@ecommerce/store";
import { AppConfigFields } from "../types/appConfigEntities";

type UiKind = "success" | "pending" | "error" | "processing";

const lower = (v?: string | null) => (v ?? "").toLowerCase();

const uiCopy = (kind: UiKind) => {
    switch (kind) {
        case "success":
            return {
                Icon: CheckIcon,
                color: "#008547",
                title: "¡Pago confirmado!",
                subtitle: "Tu pedido quedó registrado correctamente.",
            };
        case "pending":
            return {
                Icon: ClockIcon,
                color: "#6b6b6b",
                title: "Pago pendiente",
                subtitle: "Tu pedido quedó registrado. Falta completar el pago en Mercado Pago.",
            };
        case "error":
            return {
                Icon: XIcon,
                color: "#d32f2f",
                title: "No pudimos acreditar el pago",
                subtitle: "Podés reintentar el pago o elegir otro medio.",
            };
        case "processing":
        default:
            return {
                Icon: ClockIcon,
                color: "#6b6b6b",
                title: "Procesando pago",
                subtitle: "Esperando que el pago impacte en el sistema. Esto puede demorar unos segundos.",
            };
    }
};

const MercadoPagoReturnPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const sp = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const orderGuid = sp.get("o") || "";   // tu param
    const txGuid = sp.get("t") || sp.get("external_reference") || ""; // fallback
    const payment_id = sp.get("payment_id");
    const collection_id = sp.get("collection_id");

    const { clearCart } = useCart();

    const [loading, setLoading] = useState(true);
    const [dto, setDto] = useState<MercadoPagoReturnVerificacionDTO | null>(null);
    const [error, setError] = useState<string | null>(null);

    const attemptsRef = useRef(0);
    const timerRef = useRef<number | null>(null);
    const didClearCartRef = useRef(false);

    const missingParams = !orderGuid || !txGuid;

    const stopTimer = () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = null;
    };

    const scheduleNext = (seconds = 2) => {
        stopTimer();
        timerRef.current = window.setTimeout(() => {
            attemptsRef.current += 1;
            void runVerify();
        }, Math.max(500, seconds * 1000));
    };

    const resolveKind = (estado?: string | null): UiKind => {
        const st = lower(estado);
        if (st === "approved") return "success";
        if (st === "pending" || st === "in_process") return "pending";
        if (st === "rejected" || st === "cancelled") return "error";
        return "processing";
    };

    const kind = useMemo(() => resolveKind(dto?.estado ?? null), [dto?.estado]);
    const { Icon, color, title, subtitle } = useMemo(() => uiCopy(kind), [kind]);

    const contactInfo = (window as any).APP_CONFIG?.[AppConfigFields.ContactData];
    const whatsappNumber = contactInfo?.whatsapp?.replace(/\D/g, "");
    const canWhatsapp = !!whatsappNumber;

    const buildWhatsappUrl = () => {
        const rawMessage = [
            "Hola! Quería consultar por mi pedido.",
            "",
            dto?.numeroOrden ? `* Número de orden: #${dto.numeroOrden}` : `* Orden (GUID): ${orderGuid}`,
            `* Estado: ${dto?.estado ?? "desconocido"}`,
            txGuid ? `* Transacción: ${txGuid}` : "",
            "",
            "Gracias!",
        ].filter(Boolean).join("\n");

        return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(rawMessage)}`;
    };

    const finalizeIfNeeded = (payload: MercadoPagoReturnVerificacionDTO) => {
        const st = lower(payload.estado);

        // Solo limpiar carrito si la orden quedó “cerrada” del lado del usuario (approved/pending).
        // Para rejected, lo dejamos para que pueda reintentar.
        if ((st === "approved" || st === "pending") && !didClearCartRef.current) {
            didClearCartRef.current = true;
            clearCart();
            sessionStorage.removeItem("mp_failure_resume_guid");

        }
    };

    const runVerify = async () => {
        if (missingParams) return;

        // timeout global: 90s (45 intentos a 2s)
        if (attemptsRef.current > 45) {
            setLoading(false);
            setError("El pago todavía no impactó en el sistema. Probá nuevamente en unos segundos.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = await HttpMercadoPagoReturn.verificar({
                uniIdOrden: orderGuid,
                uniIdPagoProveedorTransaccion: txGuid,
                payment_id,
                collection_id,
            });

            setDto(payload);
            finalizeIfNeeded(payload);

            const st = lower(payload.estado);

            if (st === "processing") {
                // polling
                scheduleNext(2);
            } else {
                // terminales para UI: approved/pending/rejected
                stopTimer();
            }
        } catch (e: any) {
            setError(e?.message || "No se pudo verificar el estado del pago.");
            // reintento suave
            scheduleNext(3);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        attemptsRef.current = 0;
        stopTimer();

        if (!missingParams) {
            void runVerify();
        } else {
            setLoading(false);
            setError("Faltan parámetros para validar el pago. Volvé a intentar desde la tienda.");
        }

        return () => stopTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const handleBackToStore = () => navigate("/");

    const handleRetry = () => {
        attemptsRef.current = 0;
        void runVerify();
    };

    return (
        <Box sx={{ maxWidth: 800, mx: "auto", mt: 6, px: 2 }}>
            <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Icon color={color} size={30} />

                <Typography fontFamily="inherit" fontSize="18px" fontWeight={600} color={color}>
                    {title}
                </Typography>

                <Typography fontFamily="inherit" fontSize="14px" fontWeight={300} sx={{ opacity: 0.85 }}>
                    {dto?.mensaje || subtitle}
                </Typography>

                {loading && (
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                        <CircularProgress size={18} />
                        <Typography fontFamily="inherit" fontSize="13px" sx={{ opacity: 0.75 }}>
                            Verificando estado…
                        </Typography>
                    </Stack>
                )}

                {!!dto?.numeroOrden && (
                    <Typography fontFamily="inherit" fontSize="28px" fontWeight={700} color={color} sx={{ mt: 1 }}>
                        Número de orden: #{dto.numeroOrden}
                    </Typography>
                )}

                {!!dto?.detalleEstado && (
                    <Typography fontFamily="inherit" fontSize="12px" sx={{ opacity: 0.6 }}>
                        Detalle: {dto.detalleEstado}
                    </Typography>
                )}

                {!!error && (
                    <Typography fontFamily="inherit" fontSize="13px" sx={{ opacity: 0.85, color: "#d32f2f" }}>
                        {error}
                    </Typography>
                )}

                {(kind === "success" || kind === "pending") && (
                    <Typography
                        variant="body2"
                        fontFamily="inherit"
                        fontSize="14px"
                        fontWeight={300}
                        sx={{ opacity: 0.7, textAlign: "center", mt: 1 }}
                    >
                        Para consultas o gestión del pago, comunicate por WhatsApp.
                    </Typography>
                )}

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    sx={{ mt: 4, width: "100%", justifyContent: "center" }}
                >
                    {canWhatsapp && (kind === "success" || kind === "pending" || kind === "error") && (
                        <Button
                            variant="contained"
                            onClick={() => window.open(buildWhatsappUrl(), "_blank")}
                            startIcon={<WhatsApp />}
                            sx={{
                                backgroundColor: "#25D366",
                                color: "#fff",
                                fontFamily: "inherit",
                                fontWeight: 500,
                                textTransform: "none",
                                py: 1.5,
                                "&:hover": { backgroundColor: "#1ebe5d" },
                            }}
                        >
                            CONTACTAR POR WHATSAPP
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleBackToStore}
                        sx={{
                            backgroundColor: "#000",
                            color: "#fff",
                            fontFamily: "inherit",
                            fontWeight: 500,
                            textTransform: "none",
                            py: 1.5,
                            "&:hover": { backgroundColor: "#333" },
                            minWidth: 220,
                        }}
                    >
                        VOLVER A LA TIENDA
                    </Button>

                    {(kind === "processing" || !!error) && (
                        <Button
                            variant="outlined"
                            onClick={handleRetry}
                            sx={{
                                fontFamily: "inherit",
                                fontWeight: 500,
                                textTransform: "none",
                                py: 1.5,
                                minWidth: 180,
                            }}
                        >
                            REINTENTAR
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default MercadoPagoReturnPage;
















